'use client';

import { FC, useState, useContext, useEffect } from 'react';
import {
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  UploadBox,
  Alert,
  Text,
} from '@/_components';
import { Files } from './components/Files';
import { UserContext } from '@/_contexts/UserProvider';
import { uploadToStorage } from '@/_api/client-documents';
import { getClientQuestionnaires } from '@/_api/client-questionnaires';
import styles from './DocumentsUploadDialog.module.css';
import type { Questionnaire } from '@/_types/models/questionnaire';
import { DocumentsContext } from '@/_contexts/DocumentsProvider';
import type { DocumentsUploadDialogProps } from './DocumentsUploadDialog.types';
import { getUniqueFiles, removeIndexFile, validateFile } from '@/_utils/files';
import { ToastContext } from '@/_contexts/ToastProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { FileWrapper } from './components/Files/Files.types';
import { sanitizeS3Name } from '@/_utils/sanitize';

export const DocumentsUploadDialog: FC<DocumentsUploadDialogProps> = ({
  isOpen,
  onClose,
  companyId,
}) => {
  const { token, isAdvisor, selectedCompanyHash } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { openDirectory, reloadFiles } = useContext(DocumentsContext);
  const { firm } = useContext(FirmSettingsContext);

  const [questionnaires, setQuestionnaires] = useState<Array<Questionnaire>>(
    [],
  );

  useEffect(() => {
    const loadQuestionnaires = async () => {
      try {
        if (!companyId || !token) return;
        const response = await getClientQuestionnaires({ companyId, token });
        if (!response || response.error) return;
        setQuestionnaires(response);
      } catch (err) {
        // noop
      }
    };

    loadQuestionnaires();
  }, [companyId, token]);

  const [isUploading, setUploading] = useState<boolean>(false);
  const [files, setFiles] = useState<Array<FileWrapper>>([]);
  const filesEmpty: boolean = files.length === 0;

  const onDrop = async (acceptedFiles: Array<File>) => {
    const uniqueFiles = getUniqueFiles(
      files.map((wrapper) => wrapper.file),
      acceptedFiles,
    );
    setFiles(
      uniqueFiles.map((file) => ({
        file,
        name: sanitizeS3Name(file.name),
      })),
    );
  };

  const onUpload = async () => {
    try {
      if (!companyId) return;

      setUploading(true);
      for (const wrapper of files) {
        const { isValid } = await validateFile(wrapper.file);

        if (!isValid) {
          addToast(
            'error',
            'We were unable to process some of uploaded files as they appear to be INVALID. Kindly check those files and try uploading them again.',
          );
          continue;
        }

        // TODO: Fix this. Not working properly
        // if (isProtectedPDF) {
        //   addToast(
        //     'error',
        //     'The PDFs you uploaded are either password-protected or cannot be opened. Please either remove the password or upload alternative files.',
        //   );
        //   continue;
        // }

        const { error } = await uploadToStorage({
          file: wrapper.file,
          newName: wrapper.name,
          companyId,
          path: openDirectory!,
          token,
        });

        if (error) {
          addToast('error', error);
        } else {
          addToast('success', 'Upload successful');
        }
      }
    } catch (error) {
      console.error(error);
      addToast('error', 'Upload failed');
    } finally {
      reloadFiles();
      onResetState();
    }
  };

  const onRemoveFile = (index: number) => {
    const newFiles: Array<FileWrapper> = removeIndexFile(files, index);
    setFiles(newFiles);
  };

  const onEditFile = (index: number, file: FileWrapper) => {
    const newFiles: Array<FileWrapper> = [...files];
    newFiles[index] = file;

    setFiles(newFiles);
  };

  const onResetState = () => {
    onClose();
    setUploading(false);
    setFiles([]);
  };

  return (
    <Dialog isOpen={isOpen} onClose={onResetState} title="Upload Documents">
      <Dialog.Content>
        <Flex isColumn={true} gap={8}>
          {questionnaires.filter((q) => !q.completed && !q.locked).length > 0 &&
            !firm?.documents_disabled &&
            !isAdvisor && (
              <Alert type="warning">
                <Flex isColumn gap={8}>
                  <Text type="small">
                    You still have one or more open questionnaires. If you need
                    to provide a document related to any of the following
                    questionnaires, please upload it there:
                  </Text>
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: '20px',
                      listStyle: 'disc',
                    }}
                  >
                    {questionnaires
                      .filter((q) => !q.completed && !q.locked)
                      .map((questionnaire) => (
                        <li key={questionnaire.id}>
                          <Text
                            type="small"
                            underline
                            className={styles.questionnaireItem}
                            onClick={() => {
                              window.location.href = selectedCompanyHash
                                ? `/questionnaire/${selectedCompanyHash}?questionnaireId=${questionnaire.id}`
                                : `/questionnaire/?questionnaireId=${questionnaire.id}&companyId=${companyId}`; // fallback
                            }}
                          >
                            {questionnaire.formatted_name}
                          </Text>
                        </li>
                      ))}
                  </ul>
                </Flex>
              </Alert>
            )}

          <UploadBox
            onDrop={onDrop}
            isLoading={isUploading}
            allowMultiple={true}
          />
          <Files
            isLoading={isUploading}
            fileWrappers={files}
            onEditFile={onEditFile}
            onRemoveFile={onRemoveFile}
            title="Selected Files"
          />
          <ButtonGroup align="right">
            <Button intent="secondary" onClick={onResetState} value="Cancel" />
            <Button
              intent="primary"
              onClick={onUpload}
              value="Upload"
              isDisabled={isUploading || filesEmpty}
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
